// Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Constants
import { ClientFlavor, ClientType, PlanType } from "@ms/uno-constants/lib/local/AppConstants";
import { ChildView, ChildViewStatus } from "@ms/uno-constants/lib/local/ChildViewConstants";
import { LoopTelemetryScenario } from "@ms/uno-constants/lib/local/LoopConstants";
import { NewsBarNewsIds } from "@ms/uno-constants/lib/local/NewsBarConstants";
import { SubRouteType, ViewType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { UserActivitySamplingResolutionMs } from "@ms/uno-constants/lib/local/UIBehaviorConstants";
import { ViewName, ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Contexts
import { ActionCreatorLoaderContext } from "@ms/planner-container-controls/lib/local/contexts/ActionCreatorLoaderContext";
import { AgentProviderContext } from "@ms/planner-container-controls/lib/local/contexts/AgentProviderContext";
import { ConfigProviderContext } from "@ms/planner-container-controls/lib/local/contexts/ConfigProviderContext";
import { StoreLoaderContext } from "@ms/planner-container-controls/lib/local/contexts/StoreLoaderContext";
import { StoreSubscriptionManagerContext } from "@ms/planner-container-controls/lib/local/contexts/StoreSubscriptionManagerContext";
// Controls
import { AriaLiveAnnouncer, Button, Spinner } from "@fluentui/react-components";
import { LazyPortfolioView } from "@ms/planner-childview/lib/local/controls/adaptors/portfolioViewAdaptor/portfolioViewLazyLoader";
import { LazyPremiumPlanView } from "@ms/planner-childview/lib/local/controls/adaptors/premiumPlanViewAdaptor/PremiumPlanLoaders";
import { LazyCampaignCalloutContainer } from "@ms/planner-container-controls/lib/local/containers/campaignCalloutContainer/LazyCampaignCalloutContainer";
import { LazyDabContainer } from "@ms/planner-container-controls/lib/local/containers/dab/LazyDabContainer";
import { LazyPlannerDevPanelContainer } from "@ms/planner-container-controls/lib/local/containers/devTools/LazyPlannerDevPanelContainer";
import { LazyFloatingActionButtonContainer } from "@ms/planner-container-controls/lib/local/containers/fab/LazyFloatingActionButtonContainer";
import { LazyNewsBarContainer } from "@ms/planner-container-controls/lib/local/containers/newsBar/LazyNewsBarContainer";
import { LazyPlannerCopilotSidecarContainer } from "@ms/planner-container-controls/lib/local/containers/plannerCopilotSidecarContainer/LazyPlannerCopilotSidecarContainer";
import { LazySuiteNavContainer } from "@ms/planner-container-controls/lib/local/containers/suiteNavContainer/SuiteNavContainerLazyLoader";
import { LazyTeamsTabLandingExperienceContainer } from "@ms/planner-container-controls/lib/local/containers/teamsTabLandingExperience/LazyTeamsTabLandingExperienceContainer";
import { LazyCreateTeamsTabPage } from "@ms/planner-container-controls/lib/local/controls/createTeamsTabPage/CreateTeamsTabPageLazyLoader";
import { LazyNotificationBarContainer } from "@ms/planner-container-controls/lib/local/controls/notifications/LazyNotificationBarContainer";
import { LazyRemoveTeamsTabPage } from "@ms/planner-container-controls/lib/local/controls/removeTeamsTabPage/RemoveTeamsTabPageLazyLoader";
import { LazyTaskEditorContainer } from "@ms/planner-container-controls/lib/local/controls/taskEditor/LazyTaskEditorContainer";
import { useRenderTracker } from "@ms/planner-container-controls/lib/local/hooks/useRenderTracker";
import { getActiveTaskEditorPanelModal } from "@ms/planner-container-controls/lib/local/utilities/TaskEditorPanelLayoutUtilities";
import { DndDragLayer } from "@ms/planner-generic-controls/lib/local/dndDragLayer/DndDragLayer";
import { ErrorBoundary } from "@ms/planner-generic-controls/lib/local/errorBoundary/ErrorBoundary";
import { SuiteNavLoadingPlaceHolder } from "@ms/planner-generic-controls/lib/local/suiteNav/SuiteNavLoadingPlaceHolder";
import { SuspenseSpinner } from "@ms/planner-generic-controls/lib/local/suspenseSpinner/SuspenseSpinner";
import { useFullscreenSpinnerStyles } from "@ms/planner-generic-controls/lib/local/suspenseSpinner/SuspenseSpinner.styles";
import { LazyReportSection } from "@ms/planner-loop/lib/local/controls/presentational/reportPage/LazyReportSection";
import { LoopResponseSectionContainer } from "@ms/planner-loop/lib/local/controls/smart/loopResponseSection/LoopResponseSectionContainer";
import { ErrorView } from "@ms/planner-presentational-controls/lib/local/errorView/ErrorView";
import { LegacyThemeProvider } from "@ms/planner-presentational-controls/lib/local/utilities/contexts/LegacyThemeProvider";
import { PlannerThemeProvider } from "@ms/planner-presentational-controls/lib/local/utilities/contexts/PlannerThemeProvider";
import { DndDropZone } from "@ms/planner-presentational-controls/lib/local/utilities/DndUtilities";
import { LazyLeftNavContainer } from "@ms/planner-view-personal/lib/local/controls/container/leftNav/LeftNavLazyLoader";
import { LazyMyDayPage } from "@ms/planner-view-personal/lib/local/controls/container/myDayPage/MyDayPageLazyLoader";
import { LazyMyPlansPage } from "@ms/planner-view-personal/lib/local/controls/container/myPlansPage/MyPlansPageLazyLoader";
import { LazyMyPortfoliosPage } from "@ms/planner-view-personal/lib/local/controls/container/myPortfoliosPage/MyPortfoliosPageLazyLoader";
import { LazyMyTasksPage } from "@ms/planner-view-personal/lib/local/controls/container/myTasksPage/MyTasksPageLazyLoader";
import { LazyPlanPage } from "@ms/planner-view-plan/lib/local/controls/container/PlanPageLazyLoader";
import { PlannerModalLayers } from "./modalLayers/PlannerModalLayers";
import { LeftNavPlaceholder } from "./shellControls/LeftNavPlaceholder";
import { PlannerShellLayout } from "./shellControls/PlannerShellLayout";
// FluxCore
import { PlannerThemeContextProvider } from "@ms/planner-presentational-controls/lib/local/utilities/contexts/PlannerThemeContext";
import { useStores } from "@ms/planner-container-controls/lib/local/hooks/useStores";
import { LoggerContextProvider } from "@ms/planner-presentational-controls/lib/local/utilities/contexts/LoggerContext";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { LoggerContextProvider as LegacyLoggerContextProvider } from "@ms/uno-controls/lib/local/contexts/LoggerContext";
// Resources
import { loadTranslations, Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import warningDark from "@ms/uno-resources/lib/local/images/warning-stand-dark.png";
import warningLight from "@ms/uno-resources/lib/local/images/warning-stand-light.png";
// Utilities
import { getLoopDependenciesPropsGenerator } from "@ms/planner-loop/lib/local/utilities/loop/LoopDependencySynthesizerPropsUtilities";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { extractMyTasksRoutingDetailsFromSubRoute, extractPlanRoutingDetailsFromSubRoute } from "@ms/uno-routing";
import { withDndContext } from "@ms/uno-utilities/lib/local/DndUtilities";
import { getUnoMainContentId } from "@ms/uno-utilities/lib/local/IdUtilities";
import { initializeAndRegisterIcons } from "@ms/uno-utilities/lib/local/ReactUtilities";
import { hasNativeTemporalSupport } from "@ms/uno-utilities/lib/local/TemporalWrapper";
import throttle from "lodash/throttle";
import React, { Suspense, useCallback, useEffect, useReducer, useState } from "react";
import { Temporal } from "temporal-polyfill-lite";
import { useUndoRedoKeyboardHandler } from "../../hooks/useUndoRedoKeyboardHandler";
import { getThemeV8 } from "../../utilities/ThemeUtilities";
// Workers
import { Worker } from "@ms/planner-workers/lib/local/workers/Worker";
// Models
import { StatusReportVersion } from "@ms/planner-models/lib/local/client/operations/Enumerations";
import { TaskEditorRenderMode } from "@ms/planner-models/lib/local/types/TaskEditorRenderMode";
/** Throttle window (ms) for the commit-on-resize blur listener */ const CommitOnResizeThrottleMs = 200;
/**
 * Internal implementation of Planner Shell
 */ const PlannerShellInternal = (props)=>{
    const { acLoader, authModule, dispatcher, hostAdaptor, loggers, onLoad, onSuccess, storeLoader, workerLoader } = props;
    const [appContextStore, routeStore, childViewStore, tenantStore, viewStore, modalStore] = useStores([
        Store.AppContext,
        Store.Route,
        Store.ChildView,
        Store.Tenant,
        Store.View,
        Store.Modal
    ]);
    const [, forceUpdate] = useReducer((x)=>x + 1, 0);
    const [v8Theme, setV8Theme] = useState(null);
    const themeScheme = appContextStore.getAppContext().theme;
    const fullHeightSpinnerStyles = useFullscreenSpinnerStyles();
    // Hook to track the view render state
    useRenderTracker({
        name: ViewName.PlannerShell,
        partitionKey: appContextStore.getCurrentUserIdentitySet().user.id
    }, (storeLoader, configProvider)=>{
        return ViewRenderState.All;
    });
    /**
     * Update current user in user store
     */ // TODO : 2824315 : [EVO] Move updateCurrentUserInStore  in PlannerShell and CreateTaskFromMessageShell
    const updateCurrentUserInStore = useCallback(async ()=>{
        const appContext = appContextStore.getAppContext();
        const userId = appContext.userId;
        if (userId == null) {
            const msg = "This should never happen. UserId retrieved from app context is null";
            loggers.traceLogger.logTrace(0x1e25528b /* tag_4jvkl */ , TraceLevel.Error, msg);
            throw new Error(msg);
        }
        acLoader.load(ActionCreator.User).fetchUsers([
            userId
        ], true);
    }, [
        acLoader,
        appContextStore,
        loggers.traceLogger
    ]);
    /**
     * Setup the idle time tracker.
     * Update UserActivityTimestamp on user's action. But only update at most once per minute.
     * TODO: check if we can defer this after views boot completion
     */ const startUserActivityTracking = useCallback(()=>{
        const activityHandler = throttle(async ()=>{
            const timestamp = Temporal.Now.instant();
            const activityActionCreator = props.acLoader.load(ActionCreator.Activity);
            activityActionCreator.updateUserActivityTimestamp(timestamp);
        }, UserActivitySamplingResolutionMs);
        const events = [
            "click",
            "dblclick",
            "mousemove",
            "mouseup",
            "mousedown",
            "mouseenter",
            "keyup",
            "keydown",
            "keypress",
            "submit",
            "change",
            "scroll",
            "resize"
        ];
        for (const eventName of events){
            document.addEventListener(eventName, activityHandler, {
                capture: true
            });
        }
    }, [
        props.acLoader
    ]);
    /**
     * Starts the history observer worker
     */ const startHistoryObserverWorker = useCallback(()=>{
        try {
            workerLoader.load(Worker.HistoryObserver).init();
        } catch (e) {
            loggers.traceLogger.logTrace(0x1e25528c /* tag_4jvkm */ , TraceLevel.Warning, `Failed to start history observer [ErrorType=${e.name}][Error=${e.message}]`);
        }
    }, [
        workerLoader,
        loggers.traceLogger
    ]);
    // Register keyboard handlers if sensitivity labels are enabled
    useEffect(()=>{
        const EnableContentLevelSensitivityLabels = props.configProvider().flights.EnableContentLevelSensitivityLabelsEvo;
        if (!EnableContentLevelSensitivityLabels) {
            return;
        }
        const keyboardHandlersActionCreator = props.acLoader.load(ActionCreator.KeyboardHandlers);
        keyboardHandlersActionCreator.register();
    }, [
        props
    ]);
    // Register undo/redo keyboard shortcuts (Ctrl/Cmd+Z and Ctrl+Y/Cmd+Shift+Z)
    useUndoRedoKeyboardHandler(acLoader, storeLoader, props.configProvider);
    // Initialize app resources and basic setup
    useEffect(()=>{
        // Initialize resource localization
        loadTranslations(storeLoader.load(Store.AppContext).getAppContext().locale);
        if (hostAdaptor.canSetDocumentTitle()) {
            document.title = Strings.LandingPage_PlannerTitle;
        }
        loggers.traceLogger.logTrace(0x1e25528e /* tag_4jvko */ , TraceLevel.Info, "PlannerShell component notifying load.");
        onLoad();
        loggers.traceLogger.logTrace(0x1e25528d /* tag_4jvkn */ , TraceLevel.Info, "PlannerShell initialization complete. Notifying success.");
        onSuccess();
        // Run side effects
        updateCurrentUserInStore();
        startUserActivityTracking();
        startHistoryObserverWorker();
        initializeAndRegisterIcons();
    }, [
        storeLoader,
        hostAdaptor,
        loggers.traceLogger,
        onLoad,
        onSuccess,
        updateCurrentUserInStore,
        startUserActivityTracking,
        startHistoryObserverWorker
    ]);
    // The V8 theme is still needed for legacy components like premium plan and portfolio. Once these are updated to V9, we can remove it.
    // Since loading the V8 theme dynamically causes suite nav to crash, we need to load V8 on boot
    useEffect(()=>{
        const loadV8Theme = async ()=>{
            try {
                const loadedV8Theme = await getThemeV8(themeScheme, loggers.traceLogger);
                setV8Theme(loadedV8Theme);
            } catch (e) {
                loggers.traceLogger.logTrace(0x1e1487d4 /* tag_4fi5u */ , TraceLevel.Warning, `Failed to load v8 theme: ${e}`);
            }
        };
        loadV8Theme();
    }, [
        themeScheme,
        loggers.traceLogger
    ]);
    // On window resize, blur focus inside [data-commit-on-resize] so inputs commit before responsive remount.
    // Throttled (leading-edge): blur immediately on the first resize, then ignore further resizes for 200ms.
    useEffect(()=>{
        const onResize = throttle(()=>{
            const active = document.activeElement;
            if (active instanceof HTMLElement && active.closest("[data-commit-on-resize]")) {
                active.blur();
            }
        }, CommitOnResizeThrottleMs, {
            leading: true,
            trailing: false
        });
        window.addEventListener("resize", onResize);
        return ()=>{
            window.removeEventListener("resize", onResize);
            onResize.cancel();
        };
    }, []);
    /*
     * Set the theme for the app
     * @param theme The theme to set
     */ const setTheme = async (theme)=>{
        const appContextAc = acLoader.load(ActionCreator.AppContext);
        appContextAc.updateAppTheme(theme);
        try {
            const newV8Theme = await getThemeV8(theme, loggers.traceLogger);
            setV8Theme(newV8Theme);
        } catch (e) {
            loggers.traceLogger.logTrace(0x1e1487d3 /* tag_4fi5t */ , TraceLevel.Warning, `Failed to load v8 theme during theme change: ${e}`);
        }
    };
    const appInstanceId = appContextStore.getAppInstanceId();
    const fetchAccessTokenCallback = async (resource, claims)=>{
        const tokenWithExpiry = await authModule.fetchAccessTokenAsync(resource, claims);
        return tokenWithExpiry.accessToken;
    };
    const renderReportSection = (report, fileSensitivityLabelComparisonState)=>{
        const statusReportVersion = props.configProvider().flights.EnableStatusReportGenerationViaPlannerAgent ? StatusReportVersion.V2 : StatusReportVersion.V1;
        return /*#__PURE__*/ React.createElement(React.Suspense, {
            fallback: /*#__PURE__*/ React.createElement(Spinner, null)
        }, /*#__PURE__*/ React.createElement(LazyReportSection, {
            report: report,
            isReadOnly: false,
            generateLoopDependenciesProps: getLoopDependenciesPropsGenerator(appContextStore.getAppContext(), tenantStore.getCurrentTenantFeedbackSettings().data, props.configProvider, fetchAccessTokenCallback, loggers, LoopTelemetryScenario.PlannerReportsPage),
            planType: PlanType.Unified,
            statusReportVersion: statusReportVersion,
            fileSensitivityLabelComparisonState: fileSensitivityLabelComparisonState
        }));
    };
    /**
     * Renders the floating action button area inside primaryView.
     * - When EnableCopilotDAB flight is on: renders DabContainer (Copilot DAB button only;
     *   the Catalyst sidecar is rendered separately at the shell level via renderCopilotPanel).
     * - Otherwise: renders the legacy FloatingActionButtonContainer (FAB).
     */ const renderFAB = ()=>{
        const { EnableCatalystIntegration, EnableCopilotDAB, EnableEvoPMA } = props.configProvider().flights;
        // DAB (Dynamic Action Bar)
        if (EnableCatalystIntegration && EnableCopilotDAB) {
            return /*#__PURE__*/ React.createElement(Suspense, {
                fallback: null
            }, /*#__PURE__*/ React.createElement(LazyDabContainer, null));
        }
        // Legacy FAB (Floating Action Button) — still requires EnableEvoPMA
        if (!EnableEvoPMA) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: null
        }, /*#__PURE__*/ React.createElement(LazyFloatingActionButtonContainer, null));
    };
    /**
     * Callback to navigate to My Plans page
     */ const navigateToMyPlans = ()=>{
        acLoader.load(ActionCreator.Routing).navigate({
            subRouteType: SubRouteType.MyPlans,
            subRoute: []
        });
    };
    /**
     * Renders the page not found error view
     * @param isTabView Whether the current view is a tab view
     */ const renderPageNotFoundErrorView = (isTabView)=>{
        return /*#__PURE__*/ React.createElement(ErrorView, {
            imageDark: warningDark,
            imageLight: warningLight,
            errorText: Strings.Errors_PageNotFoundErrorText,
            errorSubText: Strings.Errors_PageNotFoundErrorBody,
            primaryButton: !isTabView ? /*#__PURE__*/ React.createElement(Button, {
                appearance: "primary",
                onClick: navigateToMyPlans
            }, Strings.Errors_PlanGoToMyPlansButtonText) : undefined
        });
    };
    const renderPrimaryView = ()=>{
        const subRouteType = routeStore.getCurrentSubRouteType();
        const planRoutingDetails = extractPlanRoutingDetailsFromSubRoute(routeStore.getCurrentSubRoute(), subRouteType);
        const isTabView = props.configProvider().sessionMetaData.appMetadata.clientFlavor === ClientFlavor.Tab;
        switch(subRouteType){
            case SubRouteType.BasicPlan:
                return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(LazyPlanPage, {
                    planId: planRoutingDetails.planId || "",
                    isTabView: isTabView,
                    renderReportSection: renderReportSection
                }), renderFAB());
            case SubRouteType.PremiumPlan:
                return renderLegacyPremiumPlanView();
            case SubRouteType.MyPortfolios:
                return /*#__PURE__*/ React.createElement(LazyMyPortfoliosPage, null);
            case SubRouteType.Portfolio:
                return renderPortfolioView();
            case SubRouteType.MyDay:
                return /*#__PURE__*/ React.createElement(LazyMyDayPage, null);
            case SubRouteType.MyTasks:
                {
                    const myTasksRoutingDetails = extractMyTasksRoutingDetailsFromSubRoute(routeStore.getCurrentSubRoute(), subRouteType);
                    const isNewMyTasksView = props.configProvider().flights.EnableNewMyTasksExperience && myTasksRoutingDetails?.viewType === ViewType.List;
                    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(LazyMyTasksPage, null), props.configProvider().flights.EnableCatalystForMyTasks && isNewMyTasksView && renderFAB());
                }
            case SubRouteType.ConfigTeamsTab:
                return renderCreateTeamsTabPage();
            case SubRouteType.NewTabPage:
                return renderNewTabLandingPage();
            case SubRouteType.RemoveTeamsTab:
                return renderRemoveTeamsTabPage();
            case SubRouteType.TodoList:
                return /*#__PURE__*/ React.createElement(LazyPlanPage, {
                    planId: planRoutingDetails.planId || "",
                    isTabView: isTabView
                });
            case SubRouteType.MyPlans:
                return /*#__PURE__*/ React.createElement(LazyMyPlansPage, {
                    configProvider: props.configProvider,
                    loggers: props.loggers
                });
            case SubRouteType.Publishing:
            case SubRouteType.MessageTasks:
                return renderPageNotFoundErrorView(isTabView);
            default:
                ErrorUtilities.unreachableCase(subRouteType, (log)=>{
                    loggers.traceLogger.logTrace(0x1e14f551 /* tag_4fpvr */ , TraceLevel.Error, log);
                });
                return renderPageNotFoundErrorView(isTabView);
        }
    };
    // Provide a common suspense wrapper for the page components, as most use the same spinner
    const renderPrimaryViewWithSuspense = ()=>{
        const suspenseContent = /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(SuspenseSpinner, {
                customStyles: fullHeightSpinnerStyles
            })
        }, renderPrimaryView());
        if (!props.configProvider().flights.EnableErrorBoundaries) {
            return suspenseContent;
        }
        const subRouteType = routeStore.getCurrentSubRouteType();
        const isChunkLoadError = (error)=>error.name === "ChunkLoadError" || error.message?.startsWith("Loading chunk");
        return /*#__PURE__*/ React.createElement(ErrorBoundary, {
            key: subRouteType,
            name: "PrimaryView",
            onError: createErrorBoundaryHandler(loggers),
            fallback: (error)=>{
                const isTabView = props.configProvider().sessionMetaData.appMetadata.clientFlavor === ClientFlavor.Tab;
                const sessionId = props.configProvider().sessionMetaData.appLoadCorrelationId;
                return /*#__PURE__*/ React.createElement(ErrorView, {
                    imageLight: warningLight,
                    imageDark: warningDark,
                    errorText: Strings.Errors_SomethingWentWrongErrorTitle,
                    errorSubText: Strings.Errors_SomethingWentWrongErrorBody,
                    primaryButton: isChunkLoadError(error) || isTabView ? /*#__PURE__*/ React.createElement(Button, {
                        appearance: "primary",
                        onClick: ()=>window.location.reload()
                    }, Strings.Errors_ReloadButtonText) : /*#__PURE__*/ React.createElement(Button, {
                        appearance: "primary",
                        onClick: navigateToMyPlans
                    }, Strings.Errors_PlanGoToMyPlansButtonText),
                    errorLinkDetails: sessionId ? {
                        linkText: Strings.Notifications_TechnicalDetails,
                        message: `${Strings.ErrorDialog_CorrelationIdText.replace("{0}", sessionId)}`
                    } : undefined
                });
            }
        }, suspenseContent);
    };
    const renderCreateTeamsTabPage = ()=>{
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(SuspenseSpinner, {
                customStyles: fullHeightSpinnerStyles
            })
        }, /*#__PURE__*/ React.createElement(LazyCreateTeamsTabPage, {
            teamsHostAdaptor: props.hostAdaptor
        }));
    };
    /**
     * Renders the remove teams tab page
     */ const renderRemoveTeamsTabPage = ()=>{
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(SuspenseSpinner, {
                customStyles: fullHeightSpinnerStyles
            })
        }, /*#__PURE__*/ React.createElement(LazyRemoveTeamsTabPage, {
            teamsHostAdaptor: props.hostAdaptor
        }));
    };
    const renderNewTabLandingPage = ()=>{
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(SuspenseSpinner, {
                customStyles: fullHeightSpinnerStyles
            })
        }, /*#__PURE__*/ React.createElement(LazyTeamsTabLandingExperienceContainer, null));
    };
    const renderCampaignCallout = ()=>{
        const { configProvider } = props;
        const appContentId = getUnoMainContentId(appInstanceId);
        // If the Iris campaigns are disabled, don't render the campaign callout
        if (configProvider()?.settings?.irisCampaignSettings?.disableIrisCampaignCallouts) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
        }, /*#__PURE__*/ React.createElement(LazyCampaignCalloutContainer, {
            appContentId: appContentId
        }));
    };
    const renderSuiteNav = ()=>{
        const { configProvider, isDevMode } = props;
        const clientType = configProvider().sessionMetaData.appMetadata.clientType;
        if (isDevMode || clientType !== ClientType.Web) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(SuiteNavLoadingPlaceHolder, null)
        }, /*#__PURE__*/ React.createElement(LazySuiteNavContainer, null));
    };
    const renderLeftNav = ()=>{
        const { configProvider } = props;
        const clientFlavor = configProvider().sessionMetaData.appMetadata.clientFlavor;
        if (clientFlavor !== ClientFlavor.App) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: renderLeftNavPlaceholder()
        }, /*#__PURE__*/ React.createElement(LazyLeftNavContainer, null));
    };
    const renderLegacyPremiumPlanView = ()=>{
        const status = childViewStore?.getChildViewInstantiationStatus(ChildView.PremiumPlan) ?? ChildViewStatus.NotLoaded;
        return status === ChildViewStatus.Instantiated ? /*#__PURE__*/ React.createElement(LazyPremiumPlanView, null) : /*#__PURE__*/ React.createElement(SuspenseSpinner, {
            customStyles: fullHeightSpinnerStyles
        });
    };
    const renderLeftNavPlaceholder = ()=>{
        return /*#__PURE__*/ React.createElement(LeftNavPlaceholder, null, /*#__PURE__*/ React.createElement(SuspenseSpinner, {
            customStyles: fullHeightSpinnerStyles
        }));
    };
    const renderDevTools = ()=>{
        const { serviceProviders, showDevTools } = props;
        if (!showDevTools) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
        }, /*#__PURE__*/ React.createElement(LazyPlannerDevPanelContainer, {
            serviceProviders: serviceProviders,
            dispatcher: dispatcher,
            forceUpdateApp: forceUpdate,
            currentTheme: themeScheme,
            setTheme: setTheme
        }));
    };
    const renderPortfolioView = ()=>{
        const status = childViewStore?.getChildViewInstantiationStatus(ChildView.Portfolio) ?? ChildViewStatus.NotLoaded;
        return status === ChildViewStatus.Instantiated ? /*#__PURE__*/ React.createElement(LazyPortfolioView, null) : /*#__PURE__*/ React.createElement(SuspenseSpinner, {
            customStyles: fullHeightSpinnerStyles
        });
    };
    const renderNotificationBar = ()=>{
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
        }, /*#__PURE__*/ React.createElement(LazyNotificationBarContainer, null));
    };
    const renderNewsBar = ()=>{
        const isBrowserUpgradeBannerDisabled = props.configProvider().changeGates.DisableBrowserUpgradeBanner;
        const hasNativeSupport = hasNativeTemporalSupport();
        // Only show banner if:
        // 1. Banner is not disabled via changegate
        // 2. Browser doesn't have native Temporal support
        if (isBrowserUpgradeBannerDisabled || hasNativeSupport) {
            return /*#__PURE__*/ React.createElement(React.Fragment, null);
        }
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
        }, /*#__PURE__*/ React.createElement(LazyNewsBarContainer, {
            newsId: NewsBarNewsIds.BrowserUpgrade
        }));
    };
    /**
     * Renders the task editor panel (sidecar) when in panel mode.
     * This is rendered as a persistent panel in the shell layout, not as a modal dialog.
     */ const renderTaskEditorPanel = ()=>{
        const { configProvider } = props;
        const isTaskEditorPanelEnabled = configProvider().flights.EnableTaskEditorPanel;
        // Returns the active task editor modal only when the panel occupies layout space, keeping render gating in sync with the primary-view-width calculation.
        const taskEditorModal = getActiveTaskEditorPanelModal(viewStore, modalStore, isTaskEditorPanelEnabled);
        if (!taskEditorModal) {
            return null;
        }
        return /*#__PURE__*/ React.createElement("div", {
            "data-commit-on-resize": true,
            style: {
                display: "contents"
            }
        }, /*#__PURE__*/ React.createElement(Suspense, {
            fallback: null
        }, /*#__PURE__*/ React.createElement(LazyTaskEditorContainer, {
            taskId: taskEditorModal.taskId,
            renderLoopPage: ()=>/*#__PURE__*/ React.createElement(LoopResponseSectionContainer, {
                    fetchAccessTokenCallback: fetchAccessTokenCallback,
                    taskId: taskEditorModal.taskId
                }),
            initialTab: taskEditorModal.initialTab,
            renderMode: TaskEditorRenderMode.Panel,
            showToggleRenderModeButton: true
        })));
    };
    /**
     * Renders the Copilot/Catalyst sidecar panel at the shell layout level.
     * Triggered by either:
     * - FAB flow: FAB state is "menu" with EnableCatalystIntegration (legacy, no EnableCopilotDAB flight)
     * - DAB flow: getCopilotSidecarState() is true (behind EnableCopilotDAB flight)
     * Rendered as a flex sibling of mainContentContainer for proper full-height layout.
     */ const renderCopilotPanel = ()=>{
        const { configProvider } = props;
        const { EnableCatalystIntegration, EnableCopilotDAB } = configProvider().flights;
        if (!EnableCatalystIntegration) {
            return null;
        }
        const shouldRenderSidecar = EnableCopilotDAB ? viewStore.getCopilotSidecarState().open : viewStore.getFABState().mode === "menu";
        if (!shouldRenderSidecar) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: null
        }, /*#__PURE__*/ React.createElement(LazyPlannerCopilotSidecarContainer, null));
    };
    return /*#__PURE__*/ React.createElement(PlannerThemeProvider, {
        theme: themeScheme
    }, /*#__PURE__*/ React.createElement(LegacyThemeProvider, {
        v8Theme: v8Theme
    }, /*#__PURE__*/ React.createElement(PlannerThemeContextProvider, {
        value: themeScheme
    }, /*#__PURE__*/ React.createElement(DndDragLayer, null), /*#__PURE__*/ React.createElement(PlannerShellLayout, {
        appInstanceId: appInstanceId,
        devTools: renderDevTools(),
        leftNav: renderLeftNav(),
        suiteNav: renderSuiteNav(),
        modalLayers: /*#__PURE__*/ React.createElement(PlannerModalLayers, {
            authModule: authModule
        }),
        primaryView: renderPrimaryViewWithSuspense(),
        campaignCallout: renderCampaignCallout(),
        newsBar: renderNewsBar(),
        notificationBar: renderNotificationBar(),
        taskEditorPanel: renderTaskEditorPanel(),
        copilotPanel: renderCopilotPanel()
    }))));
};
/**
 * Set the dnd backend for the Planner Shell and child views
 * TODO: implement this for child views, placeholder method for now
 * @param dndBackend The current dnd backend to update the views with
 */ const setDndBackend = ()=>{
    return Promise.resolve();
};
/**
 * Creates an onError callback for ErrorBoundary that logs to the trace logger.
 * Logs a classified error type, source component, and classification-specific metadata
 * instead of raw error messages to avoid leaking PII through telemetry.
 * @param loggers Logger context for telemetry
 */ function createErrorBoundaryHandler(loggers) {
    return (error, errorInfo, boundaryName)=>{
        const { errorClass, metadata } = ErrorUtilities.classifyError(error);
        loggers.traceLogger.logTrace(0x1e01f1c7 /* tag_4a5hh */ , TraceLevel.Error, "React render error caught by ErrorBoundary", {
            boundaryName: boundaryName ?? "Unknown",
            errorClass,
            sourceComponent: ErrorUtilities.extractComponentName(errorInfo.componentStack),
            ...metadata
        });
    };
}
export const PlannerShell = (props)=>{
    const DndWrappedPlannerShellInternal = (innerProps)=>/*#__PURE__*/ React.createElement(DndDropZone, null, /*#__PURE__*/ React.createElement(AriaLiveAnnouncer, null, /*#__PURE__*/ React.createElement(PlannerShellInternal, innerProps)));
    const WrappedComponent = withDndContext(DndWrappedPlannerShellInternal, setDndBackend);
    return /*#__PURE__*/ React.createElement(StoreSubscriptionManagerContext.Provider, {
        value: props.dispatcher
    }, /*#__PURE__*/ React.createElement(ConfigProviderContext.Provider, {
        value: props.configProvider
    }, /*#__PURE__*/ React.createElement(LegacyLoggerContextProvider, {
        value: props.loggers
    }, /*#__PURE__*/ React.createElement(LoggerContextProvider, {
        value: props.loggers
    }, /*#__PURE__*/ React.createElement(StoreLoaderContext.Provider, {
        value: props.storeLoader
    }, /*#__PURE__*/ React.createElement(ActionCreatorLoaderContext.Provider, {
        value: props.acLoader
    }, /*#__PURE__*/ React.createElement(AgentProviderContext.Provider, {
        value: props.agentProviders
    }, props.configProvider().flights.EnableErrorBoundaries ? /*#__PURE__*/ React.createElement(ErrorBoundary, {
        name: "PlannerShell",
        onError: createErrorBoundaryHandler(props.loggers),
        fallback: ()=>{
            const sessionId = props.configProvider().sessionMetaData.appLoadCorrelationId;
            return /*#__PURE__*/ React.createElement(ErrorView, {
                imageLight: warningLight,
                imageDark: warningDark,
                errorText: Strings.Errors_SomethingWentWrongErrorTitle,
                errorSubText: Strings.Errors_SomethingWentWrongErrorBody,
                primaryButton: /*#__PURE__*/ React.createElement(Button, {
                    appearance: "primary",
                    onClick: ()=>window.location.reload()
                }, Strings.Errors_ReloadButtonText),
                errorLinkDetails: sessionId ? {
                    linkText: Strings.Notifications_TechnicalDetails,
                    message: `${Strings.ErrorDialog_CorrelationIdText.replace("{0}", sessionId)}`
                } : undefined
            });
        }
    }, /*#__PURE__*/ React.createElement(WrappedComponent, props)) : /*#__PURE__*/ React.createElement(WrappedComponent, props))))))));
};
